import React, { useState } from "react";
import { useEffect } from "react";
import {
  StatisticsFilterState,
  useStatisticsFilterStore,
} from "../Stores/useStatisticsFilterStore";
import { MultiSelect } from "../../../components/UI/MultiSelect/MultiSelect";
import "./StatisticsFilterList.scss";
import { createStatisticsFilterStore } from "../Stores/statisticsFilterStoreFactory";

interface StatisticsFilterListProps {
  useStore: ReturnType<typeof createStatisticsFilterStore>;

  useTimeintervals?: boolean;
  useTypeOfBookings?: boolean;
  useTopListFilter?: boolean;
  useActivities?: boolean;
  usePermissions?: boolean;
  useCourts?: boolean;
  useGender?: boolean;
  useAgeGroups?: boolean;
  useActivityColors?: boolean;
  useCoaches?: boolean;
  useRedBlueFilter?: boolean;
}

const StatisticsFilterList = ({
  useStore,
  useTimeintervals = false,
  useTypeOfBookings = false,
  useTopListFilter = false,
  useActivities = false,
  usePermissions = false,
  useCourts = false,
  useGender = false,
  useAgeGroups = false,
  useActivityColors = false,
  useCoaches = false,
  useRedBlueFilter = false,
}: StatisticsFilterListProps) => {
  const {
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    typeOfBookings,
    setTypeOfBookings,
    typeOfBookingListSort,
    setTypeOfBookingListSort,
    courts,
    setCourts,
    selectedCourts,
    setSelectedCourts,
    permissions,
    setPermissions,
    selectedPermissions,
    setSelectedPermissions,
    coaches,
    setCoaches,
    selectedCoaches,
    setSelectedCoaches,
    genders,
    setGenders,
    ageStart,
    ageEnd,
    setAgeRange,
    resetFilters,
    activityColors,
    setActivityColors,
  } = useStore((state) => state);

  const timeIntervals = [
    "07:00",
    "07:30",
    "08:00",
    "08:30",
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
    "18:30",
    "19:00",
    "19:30",
    "20:00",
    "20:30",
    "21:00",
    "21:30",
    "22:00",
    "22:30",
  ];
  useEffect(() => {
    // Remove items not in typeOfBookings
    let next = typeOfBookingListSort.filter((v) => typeOfBookings.includes(v));

    // Add new items added to typeOfBookings
    for (const t of typeOfBookings) {
      if (!next.includes(t)) next.push(t);
    }

    setTypeOfBookingListSort(next);
  }, [typeOfBookings]);

  return (
    <div className="statistics-filter-wrapper text-m">
      <span className="secondary-text">Filtreringar:</span>

      {useTimeintervals && (
        <>
          <div className="filter-seperator" />
          <div className="statistics-filter-item">
            <label>Tidsintervall:</label>
            <select
              name="startTime"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="text-m"
            >
              {timeIntervals.map((interval, index) => (
                <option key={index} value={interval}>
                  {interval}
                </option>
              ))}
            </select>
            <span>till</span>
            <select
              name="endTime"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="text-m"
            >
              {timeIntervals.map((interval, index) => (
                <option key={index} value={interval}>
                  {interval}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-seperator" />
        </>
      )}

      {useTypeOfBookings && (
        <div className="statistics-filter-item">
          <MultiSelect
            label="Deltagare"
            options={[
              { label: "Huvudbokare", value: "BOOKER" },
              { label: "Medspelare", value: "CO_PLAYER" },
              { label: "Aktivitet", value: "ACTIVITY" },
            ]}
            selected={typeOfBookings}
            multiple
            onChange={(val) => {
              if ((val as string[]).length === 0) return; // Prevent empty selection
              setTypeOfBookings(val as any);
            }}
            formatSelected={(val) => `Deltagare (${(val as string[]).length})`}
          />
        </div>
      )}
      {useTopListFilter && (
        <div className="statistics-filter-item">
          <MultiSelect
            label="Topplista sortering"
            options={[
              { label: "Huvudbokare", value: "BOOKER" },
              { label: "Medspelare", value: "CO_PLAYER" },
              { label: "Aktivitet", value: "ACTIVITY" },
            ]}
            selected={typeOfBookingListSort}
            multiple
            onChange={(val) => {
              if ((val as string[]).length === 0) return; // Prevent empty selection

              setTypeOfBookingListSort(val as any);
            }}
            formatSelected={(val) =>
              `Topplista sortering (${(val as string[]).length})`
            }
          />
        </div>
      )}
      {useActivityColors && (
        <div className="statistics-filter-item">
          <MultiSelect
            label="Aktivitetsfärger"
            options={[
              { label: "Blå", value: "activity-color-1" },
              { label: "Orange", value: "activity-color-2" },
              { label: "Gul", value: "activity-color-3" },
              { label: "Lila", value: "activity-color-4" },
            ]}
            selected={activityColors}
            multiple
            onChange={(val) => {
              setActivityColors(val as any);
            }}
            formatSelected={(val) =>
              `Aktivitetsfärger (${(val as string[]).length})`
            }
            allButton
          />
        </div>
      )}
      {usePermissions && (
        <div className="statistics-filter-item">
          <MultiSelect
            label="Spelrätter"
            options={permissions.map((p) => ({
              label: p.member_type,
              value: p.permission_id,
            }))}
            selected={selectedPermissions.map((p) => p.permission_id)}
            multiple
            onChange={(val) => {
              const ids = val as string[];
              setSelectedPermissions(
                permissions.filter((p) => ids.includes(p.permission_id)),
              );
            }}
            formatSelected={(val) => `Spelrätter (${(val as string[]).length})`}
            allButton
          />
        </div>
      )}
      {useCourts && (
        <div className="statistics-filter-item">
          <MultiSelect
            label="Banor"
            options={courts.map((c) => ({ label: c.name, value: c.court_id }))}
            selected={selectedCourts.map((c) => c.court_id)}
            multiple
            onChange={(val) => {
              const ids = val as string[];
              setSelectedCourts(courts.filter((c) => ids.includes(c.court_id)));
            }}
            formatSelected={(val) => `Banor (${(val as string[]).length})`}
            allButton
            presetOptions={[
              {
                label: "Inne",
                values: courts
                  .filter(
                    (c) =>
                      c.name === "CC" ||
                      (/^B\d+$/.test(c.name) &&
                        parseInt(c.name.slice(1)) >= 1 &&
                        parseInt(c.name.slice(1)) <= 15 &&
                        c.name !== "B12" &&
                        c.name !== "B13"),
                  )
                  .map((c) => c.court_id),
              },
              {
                label: "Ute",
                values: courts
                  .filter(
                    (c) =>
                      /^B\d+$/.test(c.name) &&
                      parseInt(c.name.slice(1)) >= 20 &&
                      parseInt(c.name.slice(1)) <= 27,
                  )
                  .map((c) => c.court_id),
              },
            ]}
          />
        </div>
      )}
      {useCoaches && (
        <div className="statistics-filter-item">
          <MultiSelect
            label="Tränare"
            options={coaches.map((c) => ({
              label: c.first_name + " " + c.last_name,
              value: c.coach_id,
            }))}
            selected={selectedCoaches.map((c) => c.coach_id)}
            multiple
            onChange={(val) => {
              const ids = val as string[];
              setSelectedCoaches(
                coaches.filter((c) => ids.includes(c.coach_id)),
              );
            }}
            formatSelected={(val) => `Tränare (${(val as string[]).length})`}
            allButton
          />
        </div>
      )}
      {useGender && (
        <div className="statistics-filter-item">
          <MultiSelect
            label="Kön"
            options={[
              { label: "Man", value: "M" },
              { label: "Kvinna", value: "F" },
            ]}
            selected={genders}
            multiple
            onChange={(val) => {
              if ((val as string[]).length === 0) return; // Prevent empty selection
              setGenders(val as any);
            }}
            formatSelected={(val) => `Kön (${(val as string[]).length})`}
          />
        </div>
      )}
      {useAgeGroups && (
        <div className="statistics-filter-item">
          <label>Ålder:</label>
          <input
            type="number"
            min={0}
            max={100}
            value={ageStart}
            onChange={(e) => setAgeRange(Number(e.target.value), ageEnd)}
            className="text-m"
          />
          <span>till</span>
          <input
            type="number"
            min={0}
            max={100}
            value={ageEnd}
            onChange={(e) => setAgeRange(ageStart, Number(e.target.value))}
            className="text-m"
          />
        </div>
      )}
      {useRedBlueFilter && (
        <div className="statistics-filter-item">
          <MultiSelect
            label="Röda/Blåa tider"
            options={[
              { label: "Röda", value: "RED" },
              { label: "Blåa", value: "BLUE" },
            ]}
            selected={useStore.getState().redBlueFilter}
            multiple
            onChange={(val) => {
              if ((val as string[]).length === 0) return; // Prevent empty selection
              useStore.getState().setRedBlueFilter(val as any);
            }}
            formatSelected={(val) => `Röda/Blåa (${(val as string[]).length})`}
          />
        </div>
      )}
      <div className="statistics-filter-item">
        <button className={``} onClick={() => resetFilters()}>
          Återställ filter
        </button>
      </div>
    </div>
  );
};

export default StatisticsFilterList;
